import { RepresentativeStats } from './types';

export interface CenterStats {
  centerId: string;
  centerName: string;
  totalRepresentatives: number;
  totalCalls: number;
  totalCallMinutes: number;
  totalHours: number;
  averageCallDuration: number;
  urgentHelpClicks: number;
  copySummaryCount: number;
  whatsappCreationCount: number;
  checklistQuestionClicks: number;    // Question text clicked
  checklistDigDeeperClicks: number;   // Dig deeper button clicked
  checklistToggleClicks: number;      // Checkbox toggled
  objectionClicks: number; // Added
  longCalls: number; // Added
  helpfulCount: number; // Added
  unhelpfulCount: number; // Added
  startDate?: string;
  endDate?: string;
}

export class StatisticsService {
  private getBackendBaseURL(): string {
    const w: any = window as any;
    return w._BACKEND_BASE_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';
  }

  private getAuthHeader(): Record<string, string> {
    const raw = (typeof window !== 'undefined' ? localStorage.getItem('backend_access_token') : '') || '';
    const token = raw.replace(/^"+|"+$/g, '').trim();
    const looksLikeJwt = token && token.split('.').length === 3;
    return looksLikeJwt ? { Authorization: `Bearer ${token}` } : {};
  }

  async getRepresentativeStats(
    userId: string,
    startDate?: string,
    endDate?: string,
    signal?: AbortSignal,
  ): Promise<RepresentativeStats> {
    const url = new URL(`${this.getBackendBaseURL()}/statistics/representatives/${encodeURIComponent(userId)}`);

    if (startDate) url.searchParams.set('startDate', startDate);
    if (endDate) url.searchParams.set('endDate', endDate);

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
      signal,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to fetch representative stats');
    }

    return await res.json();
  }

  async getRepresentativesStatsByCenter(
    centerId?: string,
    startDate?: string,
    endDate?: string,
    signal?: AbortSignal,
  ): Promise<RepresentativeStats[]> {
    const url = new URL(`${this.getBackendBaseURL()}/statistics/representatives`);

    if (centerId) url.searchParams.set('centerId', centerId);
    if (startDate) url.searchParams.set('startDate', startDate);
    if (endDate) url.searchParams.set('endDate', endDate);

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
      signal,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to fetch representatives stats');
    }

    return await res.json();
  }

  async getTokenUsageByCenter(
    centerId?: string,
    startDate?: string,
    endDate?: string,
    signal?: AbortSignal,
  ): Promise<any[]> {
    const url = new URL(`${this.getBackendBaseURL()}/statistics/token-usage`);

    if (centerId) url.searchParams.set('centerId', centerId);
    if (startDate) url.searchParams.set('startDate', startDate);
    if (endDate) url.searchParams.set('endDate', endDate);

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
      signal,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to fetch token usage');
    }

    return await res.json();
  }

  async getTokenUsageSummaryByCenter(
    centerId?: string,
    startDate?: string,
    endDate?: string,
    signal?: AbortSignal,
  ): Promise<any> {
    const url = new URL(`${this.getBackendBaseURL()}/statistics/token-usage/summary`);

    if (centerId) url.searchParams.set('centerId', centerId);
    if (startDate) url.searchParams.set('startDate', startDate);
    if (endDate) url.searchParams.set('endDate', endDate);

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
      signal,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to fetch token usage summary');
    }

    return await res.json();
  }

  async getCentersStats(
    startDate?: string,
    endDate?: string,
    signal?: AbortSignal,
  ): Promise<CenterStats[]> {
    const url = new URL(`${this.getBackendBaseURL()}/statistics/centers`);

    if (startDate) url.searchParams.set('startDate', startDate);
    if (endDate) url.searchParams.set('endDate', endDate);

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
      signal,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to fetch centers stats');
    }

    return await res.json();
  }
  async getRepresentativeStatsHistory(
    userId: string,
    startDate: string,
    endDate: string,
    interval: 'daily' | 'weekly' | 'monthly' = 'daily',
    signal?: AbortSignal,
  ): Promise<RepresentativeStats[]> {
    const url = new URL(`${this.getBackendBaseURL()}/statistics/representatives/${encodeURIComponent(userId)}/history`);

    url.searchParams.set('startDate', startDate);
    url.searchParams.set('endDate', endDate);
    url.searchParams.set('interval', interval);

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
      signal,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to fetch representative stats history');
    }

    return await res.json();
  }

  async getCenterStatsHistory(
    centerId: string,
    startDate: string,
    endDate: string,
    interval: 'daily' | 'weekly' | 'monthly' = 'daily',
    signal?: AbortSignal,
  ): Promise<CenterStats[]> {
    const url = new URL(`${this.getBackendBaseURL()}/statistics/centers/${encodeURIComponent(centerId)}/history`);

    url.searchParams.set('startDate', startDate);
    url.searchParams.set('endDate', endDate);
    url.searchParams.set('interval', interval);

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      credentials: 'include',
      signal,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to fetch center stats history');
    }

    return await res.json();
  }
}
